/**
 * データベースユーティリティ関数
 * 共通のデータベース操作ヘルパー
 */

import {CustomerReferenceError} from "./errors";
import type {PostgrestError, SupabaseClient} from "@supabase/supabase-js";

/**
 * Supabaseエラーをハンドリング
 */
export function handleDatabaseError(error: PostgrestError | null): never {
  if (!error) {
    throw new Error("Unknown database error");
  }

  const referenceError = CustomerReferenceError.fromPostgrestError(error);
  if (referenceError) throw referenceError;

  // 一般的なエラーコードの処理
  switch (error.code) {
    case "23505": // unique_violation
      throw new Error(`重複エラー: ${error.details || error.message}`);
    case "23503": // foreign_key_violation
      throw new Error(`参照エラー: ${error.details || error.message}`);
    case "23502": // not_null_violation
      throw new Error(`必須項目エラー: ${error.details || error.message}`);
    case "42501": // insufficient_privilege
      throw new Error("権限不足エラー");
    case "PGRST301": // JWT expired
      throw new Error("セッションが期限切れです。再度ログインしてください。");
    default:
      throw new Error(`データベースエラー: ${error.message}`);
  }
}

/**
 * ページネーション用のクエリヘルパー
 */
export interface PaginationOptions {
  page?: number;
  limit?: number;
  orderBy?: string;
  ascending?: boolean;
}

export function buildPaginationQuery(query: any, options: PaginationOptions = {}): any {
  const {page = 1, limit = 20, orderBy = "created_at", ascending = false} = options;
  const from = (page - 1) * limit;
  const to = from + limit - 1;

  let paginatedQuery = query.order(orderBy, {ascending});

  if (limit > 0) {
    paginatedQuery = paginatedQuery.range(from, to);
  }

  return paginatedQuery;
}

/**
 * NULL値の安全な処理
 */
export function nullable<T>(value: T | null | undefined): T | null {
  return value === undefined ? null : value;
}

/**
 * 配列の安全な処理
 */
export function ensureArray<T>(value: T | T[] | null | undefined): T[] {
  if (value === null || value === undefined) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

/**
 * RLSポリシーエラーの判定
 */
export function isRLSError(error: PostgrestError): boolean {
  return error.code === "42501" || error.message.toLowerCase().includes("row level security");
}

/**
 * リトライ処理のヘルパー
 * @param fn 実行する関数
 * @param maxRetries 最大リトライ回数
 * @param delay 初期遅延時間（ミリ秒）
 */
export async function withRetry<T>(fn: () => Promise<T>, maxRetries: number = 3, delay: number = 1000): Promise<T> {
  let lastError: Error | null = null;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      if (i < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delay * Math.pow(2, i)));
      }
    }
  }

  throw lastError;
}

/**
 * バッチ処理のヘルパー
 */
export async function processBatch<T, R>(items: T[], processor: (item: T) => Promise<R>, batchSize: number = 10): Promise<R[]> {
  const results: R[] = [];

  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map(processor));
    results.push(...batchResults);
  }

  return results;
}

/**
 * 日付のフォーマット（ISO 8601）
 */
export function toISOString(date: Date | string | null | undefined): string | null {
  if (!date) return null;
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toISOString();
}

/**
 * キャッシュキーの生成
 */
export function buildCacheKey(prefix: string, params: Record<string, any>): string {
  const sortedParams = Object.keys(params)
    .sort()
    .map((key) => `${key}:${params[key]}`)
    .join(":");
  return `${prefix}:${sortedParams}`;
}

/**
 * 検索クエリビルダー
 */
export interface SearchOptions {
  query: string;
  fields: string[];
  filters?: Record<string, any>;
}

export function buildSearchQuery(baseQuery: any, options: SearchOptions): any {
  let query = baseQuery;

  // テキスト検索
  if (options.query) {
    const searchConditions = options.fields.map((field) => `${field}.ilike.%${options.query}%`).join(",");

    query = query.or(searchConditions);
  }

  // 追加フィルター
  if (options.filters) {
    Object.entries(options.filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          query = query.in(key, value);
        } else {
          query = query.eq(key, value);
        }
      }
    });
  }

  return query;
}

/**
 * バッチ挿入ヘルパー
 */
export async function batchInsert<T extends Record<string, any>>(supabase: SupabaseClient, table: string, records: T[], batchSize: number = 100): Promise<{inserted: number; errors: any[]}> {
  const results = {
    inserted: 0,
    errors: [] as any[],
  };

  for (let i = 0; i < records.length; i += batchSize) {
    const batch = records.slice(i, i + batchSize);

    try {
      const {data, error} = await supabase.from(table).insert(batch).select();

      if (error) {
        results.errors.push({
          batch: i / batchSize + 1,
          error,
        });
      } else if (data) {
        results.inserted += data.length;
      }
    } catch (error) {
      results.errors.push({
        batch: i / batchSize + 1,
        error,
      });
    }
  }

  return results;
}

/**
 * 一括更新ヘルパー
 */
export async function bulkUpdate<T extends Record<string, any>>(supabase: SupabaseClient, table: string, updates: Array<{id: string; data: Partial<T>}>): Promise<{updated: number; errors: any[]}> {
  const results = {
    updated: 0,
    errors: [] as any[],
  };

  for (const {id, data} of updates) {
    try {
      const {error} = await supabase.from(table).update(data).eq("id", id);

      if (error) {
        results.errors.push({id, error});
      } else {
        results.updated++;
      }
    } catch (error) {
      results.errors.push({id, error});
    }
  }

  return results;
}

/**
 * 存在チェックヘルパー
 */
export async function exists(supabase: SupabaseClient, table: string, filters: Record<string, any>): Promise<boolean> {
  let query = supabase.from(table).select("id", {count: "exact", head: true});

  Object.entries(filters).forEach(([key, value]) => {
    query = query.eq(key, value);
  });

  const {count, error} = await query;

  if (error) {
    handleDatabaseError(error);
  }

  return (count || 0) > 0;
}

/**
 * カウント取得ヘルパー
 */
export async function getCount(supabase: SupabaseClient, table: string, filters?: Record<string, any>): Promise<number> {
  let query = supabase.from(table).select("*", {count: "exact", head: true});

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      query = query.eq(key, value);
    });
  }

  const {count, error} = await query;

  if (error) {
    handleDatabaseError(error);
  }

  return count || 0;
}

/**
 * ソフトデリートヘルパー
 */
export async function softDelete(supabase: SupabaseClient, table: string, ids: string | string[]): Promise<void> {
  const idArray = Array.isArray(ids) ? ids : [ids];

  const {error} = await supabase.from(table).update({deleted_at: new Date().toISOString()}).in("id", idArray);

  if (error) {
    handleDatabaseError(error);
  }
}

/**
 * データベース接続チェック
 */
export async function checkConnection(supabase: SupabaseClient): Promise<boolean> {
  try {
    const {error} = await supabase.from("customers").select("id").limit(1);

    return !error;
  } catch {
    return false;
  }
}

/**
 * メモリキャッシュ（簡易版）
 */
const memoryCache = new Map<string, {data: any; expiry: number}>();

export async function cachedQuery<T>(
  key: string,
  queryFn: () => Promise<T>,
  ttl: number = 60000, // デフォルト60秒
): Promise<T> {
  const now = Date.now();
  const cached = memoryCache.get(key);

  if (cached && cached.expiry > now) {
    return cached.data;
  }

  const data = await queryFn();
  memoryCache.set(key, {
    data,
    expiry: now + ttl,
  });

  // 古いキャッシュのクリーンアップ
  if (memoryCache.size > 100) {
    for (const [k, v] of Array.from(memoryCache.entries())) {
      if (v.expiry < now) {
        memoryCache.delete(k);
      }
    }
  }

  return data;
}

/**
 * リトライデコレーター
 */
export function retryable<T extends (...args: any[]) => Promise<any>>(fn: T, maxRetries: number = 3, delay: number = 1000): T {
  return (async (...args: Parameters<T>) => {
    return withRetry(() => fn(...args), maxRetries, delay);
  }) as T;
}
